import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Badge,
  EmptyState,
  InfoBox,
  LoadingState,
  Pagination,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  kstMonthDayStr,
  kstTimeStr,
} from '@biznomi/ui-react'
import { auditActionLabel, auditTargetTypeLabel } from '@biznomi/types'
import { getAuditLogs } from '@/lib/api/admin'

/**
 * 감사 로그 — 누가 무엇을 언제 바꿨는가.
 *
 * 체인 검증이 "값이 바뀌었나"를 보여준다면, 여기는 "누가 바꿨나"를 보여준다.
 * 둘을 대조해야 변조와 정상 수정이 갈린다.
 *
 * ADMIN 전용 (§16-D15). 부서장이 남의 이력을 들여다보게 두면 노사 분쟁이 된다.
 */

const ALL = '__all__'

/** 자주 보는 것만. 서버는 모든 action 을 주지만 필터는 좁혀 놓는 게 쓸모 있다 */
const ACTIONS = [
  'attendance.update',
  'attendance.approve',
  'attendance.reject',
  'device.approve',
  'device.reject',
  'selfApproval.blocked',
  'nfcSpot.update',
  'employee.terminate',
] as const

const PAGE_SIZE = 50

/**
 * 시각이 어떻게 바뀌었는지 한 줄로.
 *
 * 이게 없으면 감사 로그는 "누가 뭔가 만졌다"까지만 말한다. 체인 검증에서 값이 바뀐
 * 기록을 발견했을 때 대조할 대상이 바로 이 줄이다 — 08:52 → 08:51 이 여기 있으면
 * 정상 수정이고, 없으면 앱을 거치지 않은 변경이다.
 */
function timeChange(metadata: unknown): string | null {
  const m = metadata as { before?: { timestamp?: string }; after?: { timestamp?: string } } | null
  const before = m?.before?.timestamp
  const after = m?.after?.timestamp
  if (!before || !after || before === after) return null
  return `${kstTimeStr(before)} → ${kstTimeStr(after)}`
}

export default function AuditLogs() {
  const [action, setAction] = useState(ALL)
  const [page, setPage] = useState(1)

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'audit-logs', action, page],
    queryFn: async () => {
      const res = await getAuditLogs({
        action: action === ALL ? undefined : action,
        limit: PAGE_SIZE,
        skip: (page - 1) * PAGE_SIZE,
      })
      if (!res.success || !res.data) throw new Error(res.error ?? '감사 로그를 불러오지 못했습니다')
      return res.data
    },
  })

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold">감사 로그</h1>
          <p className="mt-1 text-sm text-slate-500">누가 무엇을 언제 바꿨는지 남습니다</p>
        </div>
        {data && <p className="text-sm text-slate-500">{data.total.toLocaleString()}건</p>}
      </header>

      <Select
        value={action}
        onValueChange={(v) => {
          setAction(v)
          setPage(1)
        }}
      >
        <SelectTrigger className="h-9 w-56 text-sm">
          <SelectValue placeholder="전체 동작" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>전체 동작</SelectItem>
          {ACTIONS.map((a) => (
            <SelectItem key={a} value={a}>
              {auditActionLabel(a)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {isLoading && <LoadingState />}
      {error && <InfoBox tone="danger">{(error as Error).message}</InfoBox>}

      {data && data.items.length === 0 && (
        <div className="rounded-card bg-card">
          <EmptyState title="해당하는 기록이 없습니다." icon="search" />
        </div>
      )}

      {data && data.items.length > 0 && (
        <>
          <ul className="divide-y divide-slate-100 overflow-hidden rounded-card bg-card">
            {data.items.map((it) => (
              <li key={it.id} className="flex flex-wrap items-start justify-between gap-3 px-4 py-3">
                <div className="min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge variant={it.action === 'selfApproval.blocked' ? 'destructive' : 'outline'}>
                      {auditActionLabel(it.action)}
                    </Badge>
                    {it.targetType && (
                      <span className="text-xs text-slate-400">{auditTargetTypeLabel(it.targetType)}</span>
                    )}
                  </div>

                  {/* 서버가 사람이 읽는 라벨을 붙여준다. 원본 ID 를 그대로 보이지 않기 위한 것 */}
                  <p className="text-sm">
                    <span className="font-medium">{it.actorLabel ?? '(알 수 없음)'}</span>
                    {it.targetLabel && <span className="text-slate-500"> → {it.targetLabel}</span>}
                  </p>

                  {(() => {
                    const change = timeChange(it.metadata)
                    return change ? (
                      <p className="text-xs text-slate-500">
                        시각 <span className="font-mono tabular-nums">{change}</span>
                      </p>
                    ) : null
                  })()}
                </div>

                <span className="shrink-0 text-xs text-slate-400">
                  {kstMonthDayStr(it.createdAt)} {kstTimeStr(it.createdAt)}
                </span>
              </li>
            ))}
          </ul>

          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </>
      )}

      <p className="text-xs leading-relaxed text-slate-400">
        기록을 고치면 여기에 남습니다. 체인 검증에서 값이 바뀐 것으로 나온 건이 여기 설명되면 정상 수정이고,
        설명되지 않으면 앱을 거치지 않은 변경입니다.
      </p>
    </div>
  )
}
